import { onBeforeUnmount } from "vue";
import { addRumDuration } from "@/lib/datadog.js";

const TICK_MS = 15_000;
const IDLE_MS = 5 * 60_000;
const MIN_REPORT_MS = 1_000;
const INTERACTION_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"];

// Only the first board of a page load can count the boot as part of its load.
let firstBoardOfPageLoad = true;

const isFirstLoadOfThisPath = () => {
  const nav = performance.getEntriesByType("navigation")[0];
  return !!nav && new URL(nav.name).pathname === window.location.pathname;
};

/**
 * Two measures of the schedule board, reported as RUM duration vitals:
 * `schedule_board_load` from navigation to the first data rendered, and
 * `schedule_board_active_time` for the time someone spent on it. Active means
 * the tab is in the foreground and there was input in the last few minutes, so a
 * board left open on a screen all day does not read as use.
 *
 * Call `markReady` once the first events have rendered. Nothing here carries a
 * job, customer or technician.
 */
export function useScheduleBoardTiming(board: "v1" | "v2") {
  const firstLoad = firstBoardOfPageLoad && isFirstLoadOfThisPath();
  firstBoardOfPageLoad = false;
  const sinceOrigin = firstLoad ? 0 : performance.now();
  const startTime = performance.timeOrigin + sinceOrigin;
  let hiddenDuringLoad = document.visibilityState === "hidden";
  let ready = false;

  const markReady = () => {
    if (ready) return;
    ready = true;
    addRumDuration(
      "schedule_board_load",
      startTime,
      performance.now() - sinceOrigin,
      { board, first_load: firstLoad, hidden_during_load: hiddenDuringLoad }
    );
  };

  let activeMs = 0;
  let segmentStart = Date.now();
  let lastInput = Date.now();
  let lastTick = Date.now();

  const tick = () => {
    const now = Date.now();
    if (document.visibilityState === "visible" && now - lastInput < IDLE_MS) {
      activeMs += now - lastTick;
    }
    lastTick = now;
  };
  const flush = () => {
    tick();
    if (activeMs >= MIN_REPORT_MS) {
      addRumDuration("schedule_board_active_time", segmentStart, activeMs, {
        board
      });
    }
    activeMs = 0;
    segmentStart = Date.now();
  };
  const onInput = () => {
    lastInput = Date.now();
  };
  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      hiddenDuringLoad = hiddenDuringLoad || !ready;
      flush();
    } else {
      lastTick = Date.now();
      segmentStart = lastTick;
    }
  };

  const timer = setInterval(tick, TICK_MS);
  INTERACTION_EVENTS.forEach((name) =>
    document.addEventListener(name, onInput, { passive: true, capture: true })
  );
  document.addEventListener("visibilitychange", onVisibility);

  onBeforeUnmount(() => {
    clearInterval(timer);
    INTERACTION_EVENTS.forEach((name) =>
      document.removeEventListener(name, onInput, { capture: true })
    );
    document.removeEventListener("visibilitychange", onVisibility);
    flush();
  });

  return { markReady };
}
